import React from 'react';
import { PropTypes as PT } from 'prop-types';
import { FormattedMessage } from 'react-intl';
import _compact from 'lodash/compact';
import SVG from 'react-inlinesvg';
import audioIcon from '@lifesize/ux-assets/vectors/svgIcons/headset.svg';
import calendarIcon from '@lifesize/ux-assets/vectors/svgIcons/calendar.svg';
import cancelIcon from '@lifesize/ux-assets/vectors/svgIcons/cancel.svg';
import chatIcon from '@lifesize/ux-assets/vectors/svgIcons/chat.svg';
import favoriteIcon from '@lifesize/ux-assets/vectors/svgIcons/star.svg';
import videoIcon from '@lifesize/ux-assets/vectors/svgIcons/video.svg';
import Tooltip from 'components/Tooltip/Tooltip';
import ContactIcon from 'containers/Contacts/ContactIcon';
import classes from './ContactListItem.module.scss';
import _get from 'lodash/get';

function handleStopClick(event) {
  event.stopPropagation();
}

class ContactListItem extends React.Component {
  handleAudio = (event) => {
    event.stopPropagation();
    this.props.handleAudioCall(this.props.contact);
  };

  handleVideo = (event) => {
    event.stopPropagation();
    this.props.handleVideoCall(this.props.contact);
  };

  handleChat = (event) => {
    event.stopPropagation();
    this.props.handleChat(this.props.contact);
  };

  contactMeDirectly = () => (
    (this.props.contact.isSelf || this.props.isSelf) &&
    !this.props.noContactMeDirectlyOption
  );

  handleRemoveContact = (event) => {
    event.stopPropagation();
    this.props.handleRemove(this.props.contact.id);
  };

  handleSchedule = (event) => {
    event.stopPropagation();
    this.props.handleSchedule(this.props.contact.id);
  };

  handleSelectContact = (event) => {
    event.stopPropagation();
    this.props.handleSelectContact(this.props.contact);
  };

  getButtonTestProps = (buttonName) => {
    return this.props.dataTestTag ? { 'data-testid': `${this.props.dataTestTag}-${buttonName}` } : {};
  }

  matchText() {
    const { contact, isFirstContact, matches, isSelf, isBold } = this.props;

    const showBoldText = ((contact.isNullContact || contact.isSelf || isSelf) && isFirstContact)
      || isBold;

    if (this.contactMeDirectly()) {
      return <b><FormattedMessage id="search.schedule.self" defaultMessage="Call me directly" /></b>;
    }

    if (showBoldText) {
      return <b>{contact.name}</b>;
    }

    if (!matches || !matches.length) {
      return contact.name;
    }

    return matches.map((segment, index) => {
      if (!segment.string.length) return '';
      return segment.match ?
        (<b key={index}>{segment.string}</b>) :
        (<span key={index}>{segment.string}</span>);
    });
  }

  renderButtons() {
    const icons = [];
    const { fixTooltip, isFirstContact, isLastContact,
      noButtons, handleRemove, handleSchedule,
      handleChat, contact, handleAudioCall,
      handleVideoCall } = this.props;
    const loc = isLastContact && !isFirstContact ? 'top' : 'bottom';

    if (!noButtons) {
      // assemble the buttons in reverse order to easily identify the last button

      if (handleRemove) {
        const buttonTestProps = this.getButtonTestProps('cancel');
        icons.unshift(
          <button key="4" onClick={this.handleRemoveContact} className={classes.removeButton} {...buttonTestProps}>
            <SVG src={cancelIcon} data-testid="cancelIcon" />
            <Tooltip
              fixPosition={fixTooltip}
              label={<FormattedMessage id="contacts.tooltip.remove" defaultMessage="Remove" />}
              location={loc}
              shift="left"
            />
          </button>
        );
      }

      if (handleSchedule) {
        const buttonTestProps = this.getButtonTestProps('schedule');
        icons.unshift(
          <button key="4" onClick={this.handleSchedule} {...buttonTestProps}>
            <SVG src={calendarIcon} />
            <Tooltip
              fixPosition={fixTooltip}
              label={<FormattedMessage id="contacts.tooltip.schedule" defaultMessage="Schedule" />}
              location={loc}
              shift={!icons.length ? 'left' : ''}
            />
          </button>
        );
      }

      // Room systems don't have chat
      if (contact.isChattable && handleChat && this.props.isChatEnabled) {
        const buttonTestProps = this.getButtonTestProps('chat');
        icons.unshift(
          <button key="3" onClick={this.handleChat} {...buttonTestProps}>
            <SVG src={chatIcon} />
            <Tooltip
              fixPosition={fixTooltip}
              label={<FormattedMessage id="contacts.tooltip.chat" defaultMessage="Chat" />}
              location={loc}
              shift={!icons.length ? 'left' : ''}
            />
          </button>
        );
      }

      if (contact.isCallable) {
        if (handleAudioCall) {
          const buttonTestProps = this.getButtonTestProps('audio');
          icons.unshift(
            <button key="2" onClick={this.handleAudio} {...buttonTestProps} >
              <SVG src={audioIcon} data-testid="audioIcon" />
              <Tooltip
                fixPosition={fixTooltip}
                label={<FormattedMessage id="contacts.tooltip.audio" defaultMessage="Call with camera off" />}
                location={loc}
                shift={!icons.length ? 'left' : ''}
              />
            </button >
          );
        }
        if (handleVideoCall) {
          const buttonTestProps = this.getButtonTestProps('video');
          icons.unshift(
            <button key="1" onClick={this.handleVideo} {...buttonTestProps}>
              <SVG src={videoIcon} data-testid="videoIcon" />
              <Tooltip
                fixPosition={fixTooltip}
                label={<FormattedMessage id="contacts.tooltip.video" defaultMessage="Call with camera on" />}
                location={loc}
                shift={!icons.length ? 'left' : ''}
              />
            </button>
          );
        }
      } else {
        if (handleAudioCall) {
          const buttonTestProps = this.getButtonTestProps('audio-disabled');
          icons.unshift(
            <button key="2" className={classes.disabled} onClick={handleStopClick} {...buttonTestProps}>
              <SVG src={audioIcon} data-testid="audioIcon" />
            </button>
          );
        }
        if (handleVideoCall) {
          const buttonTestProps = this.getButtonTestProps('video-disabled');
          icons.unshift(
            <button key="1" className={classes.disabled} onClick={handleStopClick} {...buttonTestProps}>
              <SVG src={videoIcon} data-testid="videoIcon" />
            </button>
          );
        }
      }
    }
    return icons;
  }

  render() {
    const { contact, className, isFirstContact,
      handleSelectContact, shortList, selectedContact,
      isHighlighted, greyOutUnCallable, fontSize,
      presenceStyle, nameFontSize, classNameContactName,
      hideStar, noButtons, hideContacts, hideMeetings, hideRooms, hideParticipantCount, isSelf, hideIcon } = this.props;
    let participantCount = null;
    const denySelection = !!(((contact.isUser || contact.isGroupContact || contact.isPersonalContact) && hideContacts)
      || (contact.isMeeting && hideMeetings)
      || (contact.isRoomSystem && hideRooms));
    const isSelectable = Boolean(handleSelectContact) && !denySelection;
    const separateListItem = isFirstContact
      && (this.contactMeDirectly() || contact.isNullContact || contact.isSelf || isSelf);

    if (!hideParticipantCount && contact && contact.participantsCount) {
      participantCount = (
        <div className={classes.participantCount}>
          <FormattedMessage
            id="components.contact.participantsCount"
            defaultMessage="{count, plural, one {# participant} other {# participants}}"
            values={{
              count: contact.participantsCount
            }}
          />
        </div>
      );
    }
    const showFavorite = !hideStar && contact.isFavoritable;
    const contactTestProps = this.props.dataTestTag ? { 'data-testid': this.props.dataTestTag } : {};
    const contactNameLength = _get(contact, 'name.length');
    const showToolTip = shortList && (contactNameLength > 16 || contactNameLength > 32);
    const itemClasses = _compact([
      className,
      classes.contactListItem,
      shortList ? classes.shortList : '',
      (selectedContact && (selectedContact.id === contact.id))
        ? classes.selected : '',
      isSelectable ? '' : classes.notSelectable,
      separateListItem ? classes.separateItem : '',
      isHighlighted ? classes.highlighted : '',
      greyOutUnCallable ? classes.greyOutMatch : '',
      greyOutUnCallable && (!contact.isCallable && !contact.isManual) ? classes.notCallable : '',
      showToolTip ? classes.contactListItemWithToolTip : ''
    ]).join(' ');
    return (
      <div
        className={itemClasses}
        onClick={(event) => {
          if ((greyOutUnCallable && !contact.isCallable && !contact.isManual) || !isSelectable) return;
          this.handleSelectContact(event);
        }}
        style={{ fontSize }}
        {...contactTestProps}
      >
        <div className={classes.contactDetails}>
          {!hideIcon && <ContactIcon
            presenceStyle={presenceStyle}
            contact={contact}
          />}
          <div
            className={_compact([classes.contactName, classNameContactName]).join(' ')}
            style={{ fontSize: nameFontSize }}
          >
            <div className={classes.overflowContainer}>
              {this.matchText()}
            </div>
            {contact.favorite && showFavorite
              ? <div className={classes.favoriteContainer}><SVG src={favoriteIcon} className={classes.favorite} /></div>
              : null
            }
            {participantCount}
          </div>
          {contact.isManual && <span className={classes.contactNote}>
            <FormattedMessage
              id="components.contactListItem.callCustom"
              defaultMessage="(Call custom)"
            />
          </span>}
          {showToolTip && <Tooltip label={contact.name} className={classes.wrapTooltip} fixPosition={true} />}
        </div>
        {!noButtons ? (
          <div className={classes.buttons}>
            {this.renderButtons()}
          </div>
        ) : null}
      </div>
    );
  }
}

ContactListItem.propTypes = {
  className: PT.string,
  classNameContactName: PT.string,
  contact: PT.object.isRequired,
  fixTooltip: PT.bool,
  fontSize: PT.string,
  handleSelectContact: PT.func,
  handleVideoCall: PT.func,
  handleAudioCall: PT.func,
  handleChat: PT.func,
  handleRemove: PT.func,
  handleSchedule: PT.func,
  hideContacts: PT.bool,
  hideMeetings: PT.bool,
  hideRooms: PT.bool,
  hideStar: PT.bool,
  isChatEnabled: PT.bool,
  isSelf: PT.bool,
  isFirstContact: PT.bool,
  isLastContact: PT.bool,
  isHighlighted: PT.bool,
  matches: PT.array,
  nameFontSize: PT.string,
  noButtons: PT.bool,
  noContactMeDirectlyOption: PT.bool,
  selectedContact: PT.object,
  shortList: PT.bool,
  presenceStyle: PT.object,
  greyOutUnCallable: PT.bool,
  hideParticipantCount: PT.bool,
  hideIcon: PT.bool,
  isBold: PT.bool,
  dataTestTag: PT.string
};

ContactListItem.defaultProps = {
  fontSize: '20px',
  hideParticipantCount: false,
  hideIcon: false
};

export default ContactListItem;
